import { useEffect,useState } from "react";
import {
  Box,Button,CircularProgress,FormControl,MenuItem,Select,Typography
} from "@mui/material";
import {
  Navigate,Route,Routes,useLocation,useNavigate,useParams,useSearchParams
} from "react-router-dom";
import AdminTeamPlayers from "./AdminTeamPlayers";
import AdminTeamStandings from "./AdminTeamStandings";
import AdminTeamInfo from "./AdminTeamInfo";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function AdminTeam(){
  const {teamSlug}=useParams();
  const navigate=useNavigate();
  const location=useLocation();
  const [searchParams,setSearchParams]=useSearchParams();
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const category=searchParams.get("category")||"";

  const [loading,setLoading]=useState(true);
  const [team,setTeam]=useState(null);
  const [categories,setCategories]=useState([]);

  const sections=[
    {key:"players",label:"Jugadores"},
    {key:"standings",label:"Tabla"},
    {key:"info",label:"Información"}
  ];

  const activeSection=sections.find(section=>
    location.pathname.endsWith(`/${section.key}`)
  )?.key||"players";

  const loadTeam=async()=>{
    if(!teamSlug) return;

    try{
      setLoading(true);

      const response=await fetch(
        `/competition/teams/${encodeURIComponent(teamSlug)}/`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudo cargar el equipo.");
      }

      setTeam(data);

      const teamCategories=Array.isArray(data.categories)
        ?data.categories.filter(item=>item.active!==false)
        :[];

      setCategories(teamCategories);

      const categoryExists=teamCategories.some(
        item=>item.slug===category
      );

      if(!categoryExists&&teamCategories.length){
        const params=new URLSearchParams(searchParams);
        params.set("category",teamCategories[0].slug);
        setSearchParams(params,{replace:true});
      }
    }catch(error){
      setTeam(null);
      setCategories([]);
      showError(error.message||"No se pudo cargar el equipo.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadTeam();
  },[teamSlug]);

  const goTo=section=>{
    const params=new URLSearchParams(searchParams);

    navigate({
      pathname:`/admin/teams/${encodeURIComponent(teamSlug)}/${section}`,
      search:params.toString()?`?${params.toString()}`:""
    },{
      preventScrollReset:true
    });
  };

  const handleCategoryChange=event=>{
    const nextCategory=event.target.value;
    const params=new URLSearchParams(searchParams);

    params.set("category",nextCategory);

    navigate({
      pathname:location.pathname,
      search:`?${params.toString()}`
    },{
      replace:true,
      preventScrollReset:true
    });
  };

  const query=category
    ?`?category=${encodeURIComponent(category)}`
    :"";

  if(loading){
    return (
      <Box
        sx={{
          width:"100%",
          maxWidth:1300,
          mx:"auto",
          py:6,
          display:"flex",
          justifyContent:"center"
        }}
      >
        <CircularProgress size={26} sx={{color:colors.secondary}} />
      </Box>
    );
  }

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          mb:2,
          display:"flex",
          flexDirection:{xs:"column",md:"row"},
          alignItems:{xs:"stretch",md:"flex-end"},
          justifyContent:"space-between",
          gap:1.5
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},
              fontWeight:700,
              color:colors.text
            }}
          >
            Administración del equipo
          </Typography>

          <Typography
            sx={{
              mt:0.3,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            {team?.name||"Jugadores, información deportiva y datos generales."}
          </Typography>
        </Box>

        <Box
          sx={{
            width:{xs:"100%",sm:230},
            flexShrink:0
          }}
        >
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.xs,
              fontWeight:600,
              color:colors.textSecondary
            }}
          >
            Categoría
          </Typography>

          <FormControl fullWidth size="small">
            <Select
              value={category}
              onChange={handleCategoryChange}
              displayEmpty
              disabled={!categories.length}
              sx={{
                height:38,
                borderRadius:shape.radius,
                backgroundColor:colors.surface,
                color:colors.text,
                fontSize:typography.sizes.sm,
                "& .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.border
                },
                "&:hover .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                  borderColor:colors.secondary,
                  borderWidth:1
                }
              }}
            >
              {!categories.length&&(
                <MenuItem value="">
                  Sin categorías
                </MenuItem>
              )}

              {categories.map(item=>(
                <MenuItem
                  key={item.slug}
                  value={item.slug}
                  sx={{
                    fontSize:typography.sizes.sm
                  }}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          mb:2,
          display:"flex",
          flexWrap:"wrap",
          gap:0.5,
          p:0.5,
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radius,
          backgroundColor:colors.surface
        }}
      >
        {sections.map(section=>{
          const active=activeSection===section.key;

          return (
            <Button
              key={section.key}
              size="small"
              onClick={()=>goTo(section.key)}
              sx={{
                px:1.5,
                minHeight:36,
                borderRadius:shape.radiusSmall,
                color:active?colors.secondary:colors.textSecondary,
                backgroundColor:active?colors.backgroundSoft:"transparent",
                textTransform:"none",
                fontWeight:active?600:500,
                "&:hover":{
                  color:colors.secondary,
                  backgroundColor:colors.backgroundSoft
                }
              }}
            >
              {section.label}
            </Button>
          );
        })}
      </Box>

      <Routes>
        <Route
          index
          element={
            <Navigate
              to={`players${query}`}
              replace
            />
          }
        />

        <Route
          path="players"
          element={<AdminTeamPlayers />}
        />

        <Route
          path="standings"
          element={<AdminTeamStandings />}
        />

        <Route
          path="info"
          element={<AdminTeamInfo />}
        />

        <Route
          path="*"
          element={
            <Navigate
              to={`players${query}`}
              replace
            />
          }
        />
      </Routes>
    </Box>
  );
}